import { useState } from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { COUNTRIES, flagEmoji } from "@/data/countries";

export function CountrySelect({ value, onChange, testid = "country-select" }) {
  const [open, setOpen] = useState(false);
  const selected = COUNTRIES.find((c) => c.code === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          data-testid={testid}
          className="flex h-12 w-full items-center justify-between rounded-md border border-zinc-800 bg-zinc-900 px-3 text-sm text-left focus:outline-none focus:ring-2 focus:ring-[#E53E3E]"
        >
          {selected ? (
            <span className="flex items-center gap-2 truncate">
              <span className="text-base">{flagEmoji(selected.code)}</span>
              <span className="truncate">{selected.name}</span>
              <span className="text-zinc-500">{selected.dial}</span>
            </span>
          ) : (
            <span className="text-zinc-500">Select your country</span>
          )}
          <ChevronsUpDown className="w-4 h-4 text-zinc-500 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-zinc-900 border-zinc-800" align="start">
        <Command className="bg-zinc-900">
          <div className="flex items-center border-b border-zinc-800 px-3">
            <Search className="w-4 h-4 text-zinc-500 shrink-0" />
            <CommandInput placeholder="Search 190+ countries..." className="h-11 text-white" data-testid={`${testid}-search`} />
          </div>
          <CommandList className="max-h-64">
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {COUNTRIES.map((c) => (
                <CommandItem
                  key={c.code}
                  value={`${c.name} ${c.dial} ${c.code}`}
                  onSelect={() => { onChange(c); setOpen(false); }}
                  data-testid={`country-option-${c.code}`}
                  className="flex items-center gap-2 text-zinc-200 aria-selected:bg-zinc-800 cursor-pointer"
                >
                  <span className="text-base">{flagEmoji(c.code)}</span>
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="text-zinc-500 text-xs">{c.dial}</span>
                  {value === c.code && <Check className="w-4 h-4 text-[#E53E3E]" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function PhoneField({ dialCode, value, onChange, testid = "phone-input", disabled }) {
  return (
    <div className="flex">
      <span className="inline-flex items-center px-3 h-12 rounded-l-md border border-r-0 border-zinc-800 bg-zinc-800/70 text-sm text-zinc-300 min-w-[64px] justify-center" data-testid={`${testid}-dialcode`}>
        {dialCode || "+—"}
      </span>
      <Input
        type="tel"
        inputMode="tel"
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/[^\d\s-]/g, ""))}
        placeholder="Phone / WhatsApp number"
        className="h-12 rounded-l-none bg-zinc-900 border-zinc-800 focus-visible:ring-[#E53E3E]"
        data-testid={testid}
      />
    </div>
  );
}

// Labeled country + phone block reused across registration and dashboards
export function CountryPhoneBlock({ country, onCountry, phone, onPhone, idPrefix = "reg" }) {
  return (
    <>
      <div className="space-y-2">
        <Label className="text-zinc-300">Country</Label>
        <CountrySelect value={country?.code} onChange={onCountry} testid={`${idPrefix}-country-select`} />
      </div>
      <div className="space-y-2">
        <Label className="text-zinc-300">Phone / WhatsApp</Label>
        <PhoneField dialCode={country?.dial} value={phone} onChange={onPhone} testid={`${idPrefix}-phone-input`} />
      </div>
    </>
  );
}
